import { Observable } from 'rxjs';

/**
 * Converts an AbortSignal to an RxJS Observable that errors when the signal is aborted.
 * This allows integration of AbortController cancellation with RxJS observable chains.
 * 
 * The returned observable is memory-leak free:
 * - Event listener is automatically removed when the observable is unsubscribed
 * - Event listener is removed if the signal was already aborted
 * - No lingering references after completion
 * 
 * @param signal The AbortSignal to convert to an observable
 * @returns Observable that errors with DOMException('AbortError') when signal is aborted
 * 
 * @example
 * ```typescript
 * const abortController = new AbortController();
 * const abort$ = abortSignalToObservable(abortController.signal);
 * 
 * this.http.get('/api/data')
 *   .pipe(takeUntil(abort$))
 *   .subscribe(...);
 * 
 * // Later: cancel the request
 * abortController.abort();
 * ```
 */
export function abortSignalToObservable(signal: AbortSignal): Observable<never> {
  return new Observable<never>(observer => {
    // If already aborted, error immediately
    if (signal.aborted) {
      observer.error(new DOMException('Request aborted', 'AbortError'));
      return;
    }

    // Listen for abort event
    const abortHandler = () => {
      observer.error(new DOMException('Request aborted', 'AbortError'));
    };
    
    signal.addEventListener('abort', abortHandler);

    // Cleanup: remove event listener when unsubscribed
    // This prevents memory leaks
    return () => {
      signal.removeEventListener('abort', abortHandler);
    };
  });
}
